/**
 * CompanyCustomerDetailPage — every shipment + container participation
 * a single customer made via this company.
 *
 *   GET /api/cargo/me/company/customers/:customerUserId
 *   Response: { profile, participations[], shipments[], totals? }
 */
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Phone, Loader, Package, Container, MapPin, Hash, ShieldCheck,
} from 'lucide-react';
import { useMyCompany, useCompanyCustomerDetail } from '@/hooks/useCompany';
import { AppLayout } from '@/components/layout/AppLayout';
import type {
  CustomerShipmentLine, CustomerParticipationLine,
} from '@/types/companyExtras';

export default function CompanyCustomerDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customerUserId } = useParams<{ customerUserId: string }>();
  const { data: company, isLoading: companyLoading } = useMyCompany();
  const { data, isLoading } = useCompanyCustomerDetail(customerUserId || null);

  if (companyLoading || isLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16 text-text-tertiary">
          <Loader size={32} className="animate-spin" />
        </div>
      </AppLayout>
    );
  }
  if (!company) {
    navigate('/company', { replace: true });
    return null;
  }
  if (!data) {
    return (
      <AppLayout>
        <div className="bg-surface border border-border rounded-2xl p-10 text-center max-w-md mx-auto">
          <p className="text-text-secondary">{t('customer_not_found', 'الزبون غير موجود.')}</p>
        </div>
      </AppLayout>
    );
  }

  const { profile, shipments = [], participations = [] } = data;
  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ').trim();

  return (
    <AppLayout>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary"
      >
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="max-w-3xl mx-auto">
        {/* Customer header */}
        <div className="bg-surface border border-border rounded-2xl p-5 mb-4 flex items-center gap-4">
          {profile.avatar_url ? (
            <img
              src={profile.avatar_url}
              alt={fullName || profile.user_id}
              className="w-14 h-14 rounded-full object-cover"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
          ) : (
            <div className="bg-primary/10 text-primary w-14 h-14 rounded-full flex items-center justify-center font-bold text-xl">
              {(fullName || '?').charAt(0)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-lg font-bold text-text-primary truncate">
                {fullName || t('anonymous', 'مستخدم')}
              </h1>
              {profile.is_verified && (
                <span className="text-success" title={t('verified', 'موثّق')}>
                  <ShieldCheck size={16} />
                </span>
              )}
            </div>
            {profile.phone && (
              <div className="flex items-center gap-1 text-sm text-text-secondary">
                <Phone size={12} /> <span dir="ltr">{profile.phone}</span>
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-primary">
              {(shipments.length + participations.length)}
            </div>
            <div className="text-xs text-text-tertiary">{t('total_interactions', 'إجمالي التعاملات')}</div>
          </div>
        </div>

        {/* Shipments */}
        {shipments.length > 0 && (
          <section className="mb-5">
            <h2 className="text-sm font-bold text-text-secondary mb-2 flex items-center gap-2">
              <Package size={16} /> {t('shipments', 'الشحنات')} ({shipments.length})
            </h2>
            <div className="space-y-2">
              {shipments.map((s) => <ShipmentLine key={s.id} shipment={s} />)}
            </div>
          </section>
        )}

        {/* Participations */}
        {participations.length > 0 && (
          <section>
            <h2 className="text-sm font-bold text-text-secondary mb-2 flex items-center gap-2">
              <Container size={16} /> {t('container_participations', 'المشاركات في الحاويات')} ({participations.length})
            </h2>
            <div className="space-y-2">
              {participations.map((p) => <ParticipationLine key={p.id} participation={p} />)}
            </div>
          </section>
        )}

        {shipments.length === 0 && participations.length === 0 && (
          <div className="bg-surface border border-border rounded-2xl p-10 text-center">
            <p className="text-text-secondary">{t('no_activity', 'لا توجد تعاملات بعد.')}</p>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function ShipmentLine({ shipment }: { shipment: CustomerShipmentLine }) {
  const route = [shipment.origin_city, shipment.destination_city].filter(Boolean).join(' ← ');
  return (
    <Link
      to={`/shipments/${shipment.id}`}
      className="block bg-surface border border-border rounded-xl p-3 hover:border-primary/40 transition-all"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            {shipment.public_code && (
              <>
                <Hash size={11} className="text-text-tertiary" />
                <span className="font-bold text-sm text-text-primary" dir="ltr">{shipment.public_code}</span>
              </>
            )}
            {!shipment.public_code && (
              <span className="font-bold text-sm text-text-primary" dir="ltr">#{shipment.id}</span>
            )}
          </div>
          {shipment.package_description && (
            <div className="text-sm text-text-primary truncate">{shipment.package_description}</div>
          )}
          {route && (
            <div className="flex items-center gap-1 text-xs text-text-secondary mt-0.5">
              <MapPin size={11} /> {route}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-xs text-text-tertiary">{shipment.status}</div>
          {shipment.price_total_usd != null && (
            <div className="text-sm font-bold text-text-primary">
              ${Number(shipment.price_total_usd).toLocaleString()}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

function ParticipationLine({ participation }: { participation: CustomerParticipationLine }) {
  return (
    <Link
      to={`/containers/${participation.container_id}`}
      className="block bg-surface border border-border rounded-xl p-3 hover:border-primary/40 transition-all"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <Hash size={11} className="text-text-tertiary" />
            <span className="font-bold text-sm text-text-primary" dir="ltr">{participation.public_code}</span>
          </div>
          {participation.container_title && (
            <div className="text-sm text-text-secondary truncate">{participation.container_title}</div>
          )}
          <div className="text-xs text-text-tertiary mt-0.5">
            {participation.est_weight_kg != null && <span>{participation.est_weight_kg} kg</span>}
            {participation.est_cbm != null && <span> · {participation.est_cbm} m³</span>}
            {participation.declared_value_usd != null && (
              <span> · ${Number(participation.declared_value_usd).toLocaleString()}</span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-text-tertiary">{participation.status}</div>
          {participation.price_total_usd != null && (
            <div className="text-sm font-bold text-text-primary">
              ${Number(participation.price_total_usd).toLocaleString()}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
